import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';

const Ingredients = ({ user }) => {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    preco_embalagem: '',
    quantidade_embalagem: '',
    unidade_embalagem: '',
    estoque_atual: '',
    estoque_minimo: '',
  });

  useEffect(() => {
    fetchIngredients();
  }, []);

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`);
      setIngredients(response.data);
    } catch (error) {
      toast.error('Erro ao carregar ingredientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      preco_embalagem: parseFloat(formData.preco_embalagem),
      quantidade_embalagem: parseFloat(formData.quantidade_embalagem),
      estoque_atual: parseFloat(formData.estoque_atual),
      estoque_minimo: parseFloat(formData.estoque_minimo),
    };

    try {
      if (editingIngredient) {
        await axios.put(`${API}/ingredients/${editingIngredient.id}`, data);
        toast.success('Ingrediente atualizado!');
      } else {
        await axios.post(`${API}/ingredients`, data);
        toast.success('Ingrediente criado!');
      }
      setDialogOpen(false);
      resetForm();
      fetchIngredients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar ingrediente');
    }
  };

  const handleEdit = (ingredient) => {
    setEditingIngredient(ingredient);
    setFormData({
      nome: ingredient.nome,
      preco_embalagem: ingredient.preco_embalagem.toString(),
      quantidade_embalagem: ingredient.quantidade_embalagem.toString(),
      unidade_embalagem: ingredient.unidade_embalagem,
      estoque_atual: ingredient.estoque_atual.toString(),
      estoque_minimo: ingredient.estoque_minimo.toString(),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este ingrediente?')) return;
    try {
      await axios.delete(`${API}/ingredients/${id}`);
      toast.success('Ingrediente excluído!');
      fetchIngredients();
    } catch (error) {
      toast.error('Erro ao excluir ingrediente');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      preco_embalagem: '',
      quantidade_embalagem: '',
      unidade_embalagem: '',
      estoque_atual: '',
      estoque_minimo: '',
    });
    setEditingIngredient(null);
  };

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  const isAdmin = user?.role === 'Admin';
  const lowStockIngredients = ingredients.filter((ing) => ing.estoque_atual <= ing.estoque_minimo);

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-500 bg-clip-text text-transparent mb-2">
            Ingredientes
          </h1>
          <p className="text-gray-600">Controle de estoque e preços</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-pink-500 to-rose-400 hover:from-pink-600 hover:to-rose-500" data-testid="add-ingredient-button">
                <Plus className="mr-2 h-4 w-4" />
                Novo Ingrediente
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="ingredient-dialog">
              <DialogHeader>
                <DialogTitle>{editingIngredient ? 'Editar Ingrediente' : 'Novo Ingrediente'}</DialogTitle>
                <DialogDescription>Preencha os dados do ingrediente</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preco_embalagem">Preço da Embalagem (R$)</Label>
                  <Input
                    id="preco_embalagem"
                    type="number"
                    step="0.01"
                    value={formData.preco_embalagem}
                    onChange={(e) => setFormData({ ...formData, preco_embalagem: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantidade_embalagem">Quantidade na Embalagem</Label>
                  <Input
                    id="quantidade_embalagem"
                    type="number"
                    step="0.01"
                    value={formData.quantidade_embalagem}
                    onChange={(e) => setFormData({ ...formData, quantidade_embalagem: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unidade_embalagem">Unidade da Embalagem (kg, g, L, ml, un, cx)</Label>
                  <Input
                    id="unidade_embalagem"
                    value={formData.unidade_embalagem}
                    onChange={(e) => setFormData({ ...formData, unidade_embalagem: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estoque_atual">Estoque Atual</Label>
                  <Input
                    id="estoque_atual"
                    type="number"
                    step="0.01"
                    value={formData.estoque_atual}
                    onChange={(e) => setFormData({ ...formData, estoque_atual: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estoque_minimo">Estoque Mínimo</Label>
                  <Input
                    id="estoque_minimo"
                    type="number"
                    step="0.01"
                    value={formData.estoque_minimo}
                    onChange={(e) => setFormData({ ...formData, estoque_minimo: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-pink-500 to-rose-400">
                  {editingIngredient ? 'Atualizar' : 'Criar'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {lowStockIngredients.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Alerta de Estoque Baixo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-600">
              {lowStockIngredients.length} ingrediente(s) estão abaixo do estoque mínimo:
            </p>
            <ul className="mt-2 space-y-1">
              {lowStockIngredients.map((ing) => (
                <li key={ing.id} className="text-sm text-red-600">
                  • {ing.nome}: {ing.estoque_atual} {ing.unidade_embalagem} (mínimo: {ing.estoque_minimo})
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {ingredients.length === 0 ? (
        <Card className="border-pink-100 bg-white/80 backdrop-blur-sm">
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nenhum ingrediente cadastrado ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ingredients.map((ingredient) => {
            const isLowStock = ingredient.estoque_atual <= ingredient.estoque_minimo;
            return (
              <Card
                key={ingredient.id}
                className={`border-pink-100 bg-white/80 backdrop-blur-sm shadow-md hover:shadow-xl transition-shadow ${isLowStock ? 'ring-2 ring-red-300' : ''}`}
              >
                <CardHeader>
                  <CardTitle className="text-xl flex items-center justify-between">
                    <span>{ingredient.nome}</span>
                    {isLowStock && <AlertTriangle className="w-5 h-5 text-red-500" />}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">Unidade</p>
                      <p className="font-semibold">{ingredient.unidade_embalagem}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Preço Unitário</p>
                      <p className="font-semibold">R$ {ingredient.preco_unitario_real.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Estoque Atual</p>
                      <p className={`font-semibold ${isLowStock ? 'text-red-600' : 'text-green-600'}`}>
                        {ingredient.estoque_atual}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Est. Mínimo</p>
                      <p className="font-semibold">{ingredient.estoque_minimo}</p>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2 pt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(ingredient)}
                        className="flex-1 border-pink-300 hover:bg-pink-50"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(ingredient.id)}
                        className="flex-1 border-red-300 hover:bg-red-50 text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Ingredients;
